/* Per-page mushaf fonts (King Fahd Complex QCF glyph fonts, served by Quran
   Foundation). Each of the 604 pages has its own font whose glyphs are whole
   words, so the page renders exactly like the printed mushaf. Loaded lazily,
   cached by the service worker (FONT_CACHE), and registered once per session.

   Tajweed (QCF v4) ships in two colour-font formats:
   - COLRv1: one font per page whose CPAL table carries the theme palettes —
     0 = light (black ink), 1 = dark (white ink), 2 = sepia. The default is
     black ink, so the theme palette is chosen with CSS `font-palette`.
   - OpenType-SVG: colours baked in, one font per theme.
   Same choice as quran.com (src/utils/fontFaceHelper.ts): COLRv1 on every
   browser — iOS Safari included — except Firefox in dark mode, whose COLRv1
   palette support is unreliable, which gets the dark OT-SVG font. */
import type { MushafId } from '@/lib/types';
import type { PageLayout } from './api';
import type { Resolved } from '@/features/settings/theme';

const isFirefox = () => typeof navigator !== 'undefined' && /firefox|fxios/i.test(navigator.userAgent);
const useSvg = (theme: Resolved) => isFirefox() && theme === 'dark';

const BASE = 'https://verses.quran.foundation/fonts/quran/hafs';

export const MUSHAFS: { id: MushafId; name: string; desc: string; glyph: boolean }[] = [
  { id: 'madani-v2', name: 'Madani 1421 (King Fahd)', desc: '15-line Madinah mushaf, crisp modern glyphs', glyph: true },
  { id: 'madani-v1', name: 'Madani 1405', desc: 'The classic 15-line Madinah print', glyph: true },
  { id: 'tajweed', name: 'Tajweed — Madinah page', desc: 'Colour-coded tajweed in the 15-line page layout', glyph: true },
  { id: 'tajweed-text', name: 'Tajweed — large text', desc: 'Colour-coded tajweed, resizable, ideal for learning', glyph: false },
  { id: 'uthmani', name: 'Uthmani Hafs (reflowable)', desc: 'Digital KFGQPC script — resize freely', glyph: false },
  { id: 'indopak', name: 'IndoPak (Nastaleeq)', desc: 'South-Asian script with waqf marks', glyph: false },
];

export const isGlyphMushaf = (m: MushafId) => m === 'madani-v1' || m === 'madani-v2' || m === 'tajweed';
export const isTajweed = (m: MushafId) => m === 'tajweed' || m === 'tajweed-text';
/** Whose page breaks a muṣḥaf follows: the 1405 print for Madani 1405, the 1421 print for the rest
    (QCF v2, the tajweed pages — same glyphs — and the King Fahd Uthmani text). */
export const layoutOf = (m: MushafId): PageLayout => (m === 'madani-v1' ? 'v1' : 'v2');

const COLR_PALETTE: Record<Resolved, number> = { light: 0, dark: 1, sepia: 2 };

export function pageFontFamily(m: MushafId, page: number, theme: Resolved = 'dark') {
  if (m === 'tajweed') return useSvg(theme) ? `qcf-t-${theme}-p${page}` : `qcf-t-p${page}`;
  return `qcf-${m === 'madani-v1' ? 'v1' : 'v2'}-p${page}`;
}

/** Inline style for a glyph run: the family plus, for COLRv1 tajweed, the theme palette. */
export function pageFontStyle(m: MushafId, page: number, theme: Resolved = 'dark'): React.CSSProperties {
  const style: React.CSSProperties = { fontFamily: pageFontFamily(m, page, theme) };
  if (m === 'tajweed' && !useSvg(theme)) style.fontPalette = `--ftu-t${page}-${theme}`;
  return style;
}

function fontUrl(m: MushafId, page: number, theme: Resolved) {
  if (m === 'madani-v1') return `${BASE}/v1/woff2/p${page}.woff2`;
  if (m === 'madani-v2') return `${BASE}/v2/woff2/p${page}.woff2`;
  return useSvg(theme) ? `${BASE}/v4/ot-svg/${theme}/woff2/p${page}.woff2` : `${BASE}/v4/colrv1/woff2/p${page}.woff2`;
}

/* @font-palette-values is tied to a family name, so each COLRv1 page font gets
   its own three palette rules. They're written as stylesheet TEXT, the way
   quran.com does it: WebKit rejects @font-palette-values through
   CSSStyleSheet.insertRule, which silently left black ink in dark mode. */
let paletteEl: HTMLStyleElement | null = null;
const registered = new Set<string>();
function registerPalettes(family: string, page: number) {
  if (registered.has(family)) return;
  registered.add(family);
  if (!paletteEl) {
    paletteEl = document.createElement('style');
    paletteEl.id = 'ftu-tajweed-palettes';
    document.head.appendChild(paletteEl);
  }
  paletteEl.appendChild(document.createTextNode(
    Object.entries(COLR_PALETTE).map(([theme, idx]) => `@font-palette-values --ftu-t${page}-${theme} { font-family: '${family}'; base-palette: ${idx}; }
`).join(''),
  ));
}

const loading = new Map<string, Promise<void>>();

export function loadPageFont(m: MushafId, page: number, theme: Resolved = 'dark'): Promise<void> {
  if (!isGlyphMushaf(m)) return Promise.resolve();
  const family = pageFontFamily(m, page, theme);
  if (loading.has(family)) return loading.get(family)!;
  const p = (async () => {
    // Register the palette rules before the face, so the first paint is already themed.
    if (m === 'tajweed' && !useSvg(theme)) registerPalettes(family, page);
    const face = new FontFace(family, `url(${fontUrl(m, page, theme)}) format('woff2')`, { display: 'block' });
    await face.load();
    document.fonts.add(face);
  })().catch((e) => { loading.delete(family); throw e; });
  loading.set(family, p);
  return p;
}

/** Glyph code for a word in the chosen mushaf (tajweed v4 shares v2 codes). */
export const glyphOf = (m: MushafId, w: { code_v1: string; code_v2: string }) => (m === 'madani-v1' ? w.code_v1 : w.code_v2);

/* ── Tajweed colour key ───────────────────────────────────────────────
   Same scheme as the colour font (Dar al-Maʿrifah tajweed mushaf), so the page
   and text views agree. Classes are quran.com's text_uthmani_tajweed tags; the
   colours are CSS variables (--tj-*) with light/dark/sepia values in app.css. */
export const TAJWEED_RULES: { key: string; cls: string[]; name: string; ar: string; desc: string }[] = [
  { key: 'silent', cls: ['ham_wasl', 'slnt', 'laam_shamsiyah', 'idgham_wo_ghunnah', 'idgham_mutajanisayn', 'idgham_mutaqaribayn'], name: 'Not pronounced', ar: 'لا يُلفظ', desc: 'Hamzat al-waṣl, silent letters, the solar lām, and letters merged away in idghām without ghunnah.' },
  { key: 'ghunnah', cls: ['ghunnah', 'ikhafa', 'ikhafa_shafawi', 'iqlab', 'idgham_ghunnah', 'idgham_shafawi'], name: 'Ghunnah — 2 counts', ar: 'غنة', desc: 'Nasalise for two counts: nūn/mīm with shaddah, ikhfāʾ, iqlāb and idghām with ghunnah.' },
  { key: 'qalqalah', cls: ['qalaqah'], name: 'Qalqalah', ar: 'قلقلة', desc: 'A light echo on ق ط ب ج د when they carry sukūn.' },
  { key: 'madd2', cls: ['madda_normal'], name: 'Natural madd — 2', ar: 'مد طبيعي', desc: 'Prolong for 2 counts. Coloured where the muṣḥaf writes the madd as a small letter (ٰ ۥ ۦ); a full madd letter (ا و ي) is left plain.' },
  { key: 'madd246', cls: ['madda_permissible'], name: 'Madd at a stop — 2, 4 or 6', ar: 'مد عارض للسكون', desc: 'At the end of an āyah, when you stop on it: the madd before its last letter (ʿāriḍ li-s-sukūn).' },
  { key: 'madd45', cls: ['madda_obligatory'], name: 'Madd before hamzah — 4 or 5', ar: 'مد متصل ومنفصل', desc: 'A hamzah follows the madd in the same word (muttaṣil) or begins the next word (munfaṣil); in Ḥafṣ both are 4 or 5 counts.' },
  { key: 'madd6', cls: ['madda_necessary'], name: 'Necessary madd — 6', ar: 'مد لازم', desc: 'Always 6 counts: a shaddah or sukūn follows the madd in the same word (ٱلضَّآلِّينَ), and in some opening letters (الٓمٓ).' },
];

export const TAJWEED_CLASS_TO_KEY: Record<string, string> = Object.fromEntries(TAJWEED_RULES.flatMap((r) => r.cls.map((c) => [c, r.key])));
